(function (window, document) {
  'use strict';

  if (window.GMB2BFacilityCatalog) {
    window.GMB2BFacilityCatalog.mountAll(document);
    return;
  }

  function parseImages(section) {
    var script = section.querySelector('[data-b2b-catalog-images]');
    if (!script) return [];

    try {
      return JSON.parse(script.textContent || '[]');
    } catch (error) {
      console.error('B2B catalog: failed to parse image list.', error);
      return [];
    }
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function preloadImage(url) {
    return new Promise(function (resolve) {
      if (!url) {
        resolve(null);
        return;
      }

      var image = new Image();

      image.onload = function () {
        resolve(url);
      };

      image.onerror = function () {
        resolve(null);
      };

      image.src = url;
    });
  }

  function Catalog(section) {
    this.section = section;
    this.sectionId = section.getAttribute('data-section-id');
    this.heading = section.getAttribute('data-heading') || 'Catalog preview';
    this.loadingText = section.getAttribute('data-loading-text') || 'Loading catalog preview...';
    this.emptyText =
      section.getAttribute('data-empty-text') ||
      'Catalog images are not available yet. Please use the PDF button or contact us for the full materials.';
    this.book = section.querySelector('[data-b2b-catalog-book]');
    this.prevButton = section.querySelector('[data-b2b-catalog-prev]');
    this.nextButton = section.querySelector('[data-b2b-catalog-next]');
    this.pageLabel = section.querySelector('[data-b2b-catalog-page-label]');
    this.progressBar = section.querySelector('[data-b2b-catalog-progress]');
    this.zoomOutButton = section.querySelector('[data-b2b-catalog-zoom-out]');
    this.zoomResetButton = section.querySelector('[data-b2b-catalog-zoom-reset]');
    this.zoomInButton = section.querySelector('[data-b2b-catalog-zoom-in]');
    this.zoomLabel = section.querySelector('[data-b2b-catalog-zoom-label]');
    this.imageUrls = parseImages(section);
    this.currentIndex = 0;
    this.zoomLevel = 1;
    this.loadedImages = {};
    this.loadingPromises = {};
    this.destroyed = false;
    this.initialized = false;
    this.flipBook = null;
    this.usingFlipBook = false;
    this.usingMobileScroller = false;
    this.mobileStage = null;
    this.scrollFrame = null;
    this.snapTimer = null;
    this.mobilePointerActive = false;
    this.mobileUsesTouchEvents = false;
    this.mobileQuery = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;
    this.observer = null;
    this.boundPrev = this.goPrev.bind(this);
    this.boundNext = this.goNext.bind(this);
    this.boundZoomOut = this.zoomOut.bind(this);
    this.boundZoomReset = this.zoomReset.bind(this);
    this.boundZoomIn = this.zoomIn.bind(this);
    this.boundMobileScroll = this.handleMobileScroll.bind(this);
    this.boundMobilePointerStart = this.handleMobilePointerStart.bind(this);
    this.boundMobilePointerEnd = this.handleMobilePointerEnd.bind(this);
    this.boundViewportChange = this.handleViewportChange.bind(this);
  }

  Catalog.prototype.mount = function () {
    if (!this.book) return;

    this.section.dataset.catalogReady = 'false';

    if (this.prevButton) {
      this.prevButton.addEventListener('click', this.boundPrev);
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.boundNext);
    }

    if (this.zoomOutButton) {
      this.zoomOutButton.addEventListener('click', this.boundZoomOut);
    }

    if (this.zoomResetButton) {
      this.zoomResetButton.addEventListener('click', this.boundZoomReset);
    }

    if (this.zoomInButton) {
      this.zoomInButton.addEventListener('click', this.boundZoomIn);
    }

    if (this.mobileQuery) {
      if (typeof this.mobileQuery.addEventListener === 'function') {
        this.mobileQuery.addEventListener('change', this.boundViewportChange);
      } else if (typeof this.mobileQuery.addListener === 'function') {
        this.mobileQuery.addListener(this.boundViewportChange);
      }
    } else {
      window.addEventListener('resize', this.boundViewportChange);
    }

    if (!this.imageUrls.length) {
      this.renderState(this.emptyText);
      this.syncUi();
      return;
    }

    this.renderState(this.loadingText);
    this.syncUi();

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        this.handleIntersection.bind(this),
        {
          rootMargin: '240px 0px'
        }
      );
      this.observer.observe(this.section);
    } else {
      this.initialize();
    }
  };

  Catalog.prototype.handleIntersection = function (entries) {
    for (var i = 0; i < entries.length; i++) {
      if (entries[i].isIntersecting) {
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
        this.initialize();
        return;
      }
    }
  };

  Catalog.prototype.initialize = function () {
    if (this.initialized || this.destroyed) return;

    this.initialized = true;

    if (this.shouldUseMobileScroller()) {
      this.renderMobileScroller(this.currentIndex);
      return;
    }

    if (this.canUsePageFlip()) {
      this.renderFlipBook();
      return;
    }

    this.loadAndRender(this.currentIndex);
  };

  Catalog.prototype.canUsePageFlip = function () {
    return !!(window.St && typeof window.St.PageFlip === 'function');
  };

  Catalog.prototype.shouldUseMobileScroller = function () {
    if (this.mobileQuery) {
      return this.mobileQuery.matches;
    }

    return window.innerWidth < 750;
  };

  Catalog.prototype.getViewStarts = function () {
    var total = this.imageUrls.length;
    var starts = [];
    var index = 0;

    if (!total) return starts;

    starts.push(0);

    if (total === 1) {
      return starts;
    }

    index = 1;

    while (index < total - 1) {
      starts.push(index);

      if (index + 1 < total - 1) {
        index += 2;
      } else {
        index += 1;
      }
    }

    if (starts[starts.length - 1] !== total - 1) {
      starts.push(total - 1);
    }

    return starts;
  };

  Catalog.prototype.getViewPosition = function (index) {
    var starts = this.getViewStarts();
    var normalized = this.normalizeIndex(index);

    for (var i = 0; i < starts.length; i++) {
      if (starts[i] === normalized) {
        return i;
      }
    }

    return 0;
  };

  Catalog.prototype.normalizeIndex = function (index) {
    var starts = this.getViewStarts();

    if (!starts.length) {
      return 0;
    }

    if (index <= starts[0]) {
      return starts[0];
    }

    for (var i = 0; i < starts.length; i++) {
      if (starts[i] === index) {
        return starts[i];
      }

      if (starts[i] > index) {
        return starts[i - 1];
      }
    }

    return starts[starts.length - 1];
  };

  Catalog.prototype.getVisibleIndexes = function (index) {
    var total = this.imageUrls.length;
    var startIndex = this.normalizeIndex(index);

    if (!total) {
      return [];
    }

    if (startIndex <= 0 || startIndex >= total - 1) {
      return [startIndex];
    }

    if (startIndex + 1 < total - 1) {
      return [startIndex, startIndex + 1];
    }

    return [startIndex];
  };

  Catalog.prototype.getPrevStartIndex = function () {
    var starts = this.getViewStarts();
    var position = this.getViewPosition(this.currentIndex);

    if (!starts.length || position <= 0) {
      return this.normalizeIndex(this.currentIndex);
    }

    return starts[position - 1];
  };

  Catalog.prototype.getNextStartIndex = function () {
    var starts = this.getViewStarts();
    var position = this.getViewPosition(this.currentIndex);

    if (!starts.length || position >= starts.length - 1) {
      return this.normalizeIndex(this.currentIndex);
    }

    return starts[position + 1];
  };

  Catalog.prototype.loadImageAt = function (index) {
    var self = this;

    if (index < 0 || index >= this.imageUrls.length) {
      return Promise.resolve(null);
    }

    if (this.loadedImages[index]) {
      return Promise.resolve(this.loadedImages[index]);
    }

    if (this.loadingPromises[index]) {
      return this.loadingPromises[index];
    }

    this.loadingPromises[index] = preloadImage(this.imageUrls[index]).then(function (loadedUrl) {
      delete self.loadingPromises[index];

      if (loadedUrl) {
        self.loadedImages[index] = loadedUrl;
      }

      return loadedUrl;
    });

    return this.loadingPromises[index];
  };

  Catalog.prototype.loadAndRender = function (index) {
    var self = this;
    var visibleIndexes;
    var loadPromises = [];

    if (this.usingMobileScroller) {
      this.destroyMobileScroller();
    }

    if (!this.imageUrls.length) {
      this.renderState(this.emptyText);
      this.syncUi();
      return;
    }

    this.currentIndex = this.normalizeIndex(clamp(index, 0, this.imageUrls.length - 1));
    visibleIndexes = this.getVisibleIndexes(this.currentIndex);
    this.renderState(this.loadingText);
    this.syncUi();

    for (var i = 0; i < visibleIndexes.length; i++) {
      loadPromises.push(self.loadImageAt(visibleIndexes[i]));
    }

    Promise.all(loadPromises).then(function (loadedUrls) {
      var pages = [];

      if (self.destroyed) return;

      for (var j = 0; j < loadedUrls.length; j++) {
        if (loadedUrls[j]) {
          pages.push({
            index: visibleIndexes[j],
            url: loadedUrls[j]
          });
        }
      }

      if (!pages.length) {
        self.renderState(self.emptyText);
        self.section.dataset.catalogReady = 'false';
        self.syncUi();
        return;
      }

      self.renderPages(pages);
      self.section.dataset.catalogReady = 'true';
      self.syncUi();
      self.preloadAdjacent();
    });
  };

  Catalog.prototype.preloadAdjacent = function () {
    var indexesToPreload = [];
    var previousView = this.getVisibleIndexes(this.getPrevStartIndex());
    var currentView = this.getVisibleIndexes(this.currentIndex);
    var nextView = this.getVisibleIndexes(this.getNextStartIndex());

    indexesToPreload = indexesToPreload.concat(previousView, currentView, nextView);

    for (var i = 0; i < indexesToPreload.length; i++) {
      this.loadImageAt(indexesToPreload[i]);
    }
  };

  Catalog.prototype.handleViewportChange = function () {
    var currentIndex;

    if (!this.initialized || this.destroyed) return;

    currentIndex = this.usingFlipBook ? this.normalizeIndexFromPageFlip(this.getPageFlipIndex()) : this.currentIndex;
    this.currentIndex = clamp(currentIndex, 0, Math.max(this.imageUrls.length - 1, 0));

    if (this.shouldUseMobileScroller()) {
      if (!this.usingMobileScroller) {
        this.renderMobileScroller(this.currentIndex);
      }
      return;
    }

    if (this.usingMobileScroller) {
      if (this.canUsePageFlip()) {
        this.renderFlipBook();
      } else {
        this.loadAndRender(this.currentIndex);
      }
    }
  };

  Catalog.prototype.renderMobileScroller = function (index) {
    var stage;
    var starts;

    if (!this.book || !this.imageUrls.length) {
      this.renderState(this.emptyText);
      this.syncUi();
      return;
    }

    this.destroyFlipBook();
    this.destroyMobileScroller();
    this.book.innerHTML = '';

    stage = document.createElement('div');
    stage.className = 'gm-b2b-catalog__mobile-stage';
    starts = this.getViewStarts();

    for (var i = 0; i < starts.length; i++) {
      var visibleIndexes = this.getVisibleIndexes(starts[i]);
      var spread = document.createElement('div');
      spread.className = 'gm-b2b-catalog__mobile-spread';
      spread.setAttribute('data-b2b-catalog-mobile-page', starts[i]);

      if (visibleIndexes.length > 1) {
        spread.classList.add('gm-b2b-catalog__mobile-spread--facing');
      } else {
        spread.classList.add('gm-b2b-catalog__mobile-spread--single');
      }

      for (var j = 0; j < visibleIndexes.length; j++) {
        var pageIndex = visibleIndexes[j];
        var page = document.createElement('div');
        page.className = 'gm-b2b-catalog__mobile-page gm-b2b-media';

        var image = document.createElement('img');
        image.src = this.imageUrls[pageIndex];
        image.alt = this.heading + ' ' + (pageIndex + 1);
        image.loading = pageIndex <= 2 ? 'eager' : 'lazy';
        image.decoding = 'async';

        page.appendChild(image);
        spread.appendChild(page);
      }

      stage.appendChild(spread);
    }

    this.mobileUsesTouchEvents = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    stage.addEventListener('scroll', this.boundMobileScroll, { passive: true });

    if (this.mobileUsesTouchEvents) {
      stage.addEventListener('touchstart', this.boundMobilePointerStart, { passive: true });
      stage.addEventListener('touchend', this.boundMobilePointerEnd, { passive: true });
      stage.addEventListener('touchcancel', this.boundMobilePointerEnd, { passive: true });
    } else {
      stage.addEventListener('pointerdown', this.boundMobilePointerStart, { passive: true });
      stage.addEventListener('pointerup', this.boundMobilePointerEnd, { passive: true });
      stage.addEventListener('pointercancel', this.boundMobilePointerEnd, { passive: true });
    }
    this.book.appendChild(stage);
    this.mobileStage = stage;
    this.usingMobileScroller = true;
    this.usingFlipBook = false;
    this.currentIndex = this.normalizeIndex(clamp(index || 0, 0, this.imageUrls.length - 1));
    this.section.dataset.catalogReady = 'true';
    this.syncUi();

    window.requestAnimationFrame(this.scrollMobileToCurrent.bind(this));
  };

  Catalog.prototype.destroyMobileScroller = function () {
    if (this.mobileStage) {
      this.mobileStage.removeEventListener('scroll', this.boundMobileScroll);

      if (this.mobileUsesTouchEvents) {
        this.mobileStage.removeEventListener('touchstart', this.boundMobilePointerStart);
        this.mobileStage.removeEventListener('touchend', this.boundMobilePointerEnd);
        this.mobileStage.removeEventListener('touchcancel', this.boundMobilePointerEnd);
      } else {
        this.mobileStage.removeEventListener('pointerdown', this.boundMobilePointerStart);
        this.mobileStage.removeEventListener('pointerup', this.boundMobilePointerEnd);
        this.mobileStage.removeEventListener('pointercancel', this.boundMobilePointerEnd);
      }
    }

    if (this.scrollFrame) {
      window.cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
    }

    if (this.snapTimer) {
      window.clearTimeout(this.snapTimer);
      this.snapTimer = null;
    }

    this.mobileStage = null;
    this.usingMobileScroller = false;
    this.mobilePointerActive = false;
    this.mobileUsesTouchEvents = false;
  };

  Catalog.prototype.scrollMobileToCurrent = function () {
    var page;

    if (!this.mobileStage) return;

    page = this.mobileStage.querySelector('[data-b2b-catalog-mobile-page="' + this.normalizeIndex(this.currentIndex) + '"]');
    if (!page) return;

    this.mobileStage.scrollTo({
      left: page.offsetLeft,
      behavior: 'smooth'
    });
  };

  Catalog.prototype.getMobileSpreadAnchors = function () {
    var anchors = [];

    if (!this.mobileStage) return anchors;

    var pages = this.mobileStage.querySelectorAll('[data-b2b-catalog-mobile-page]');
    for (var i = 0; i < pages.length; i++) {
      anchors.push({
        index: parseInt(pages[i].getAttribute('data-b2b-catalog-mobile-page'), 10),
        left: pages[i].offsetLeft,
        width: pages[i].offsetWidth
      });
    }

    return anchors;
  };

  Catalog.prototype.updateMobileIndexFromScroll = function () {
    var anchors = this.getMobileSpreadAnchors();
    var scrollLeft = this.mobileStage ? this.mobileStage.scrollLeft : 0;
    var activeIndex = this.currentIndex;

    for (var i = 0; i < anchors.length; i++) {
      var nextLeft = i + 1 < anchors.length ? anchors[i + 1].left : Infinity;

      if (scrollLeft >= anchors[i].left - 1 && scrollLeft < nextLeft - 1) {
        activeIndex = anchors[i].index;
        break;
      }
    }

    if (!isNaN(activeIndex) && activeIndex !== this.currentIndex) {
      this.currentIndex = this.normalizeIndex(activeIndex);
      this.syncUi();
    }
  };

  Catalog.prototype.snapMobileToSpreadEdge = function () {
    var anchors = this.getMobileSpreadAnchors();
    var scrollLeft = this.mobileStage ? this.mobileStage.scrollLeft : 0;

    if (!this.mobileStage || !anchors.length) return;

    for (var i = 0; i < anchors.length; i++) {
      var contentStart = anchors[i].left;
      var contentEnd = anchors[i].left + anchors[i].width;
      var endLeft = Math.max(contentStart, contentEnd - this.mobileStage.clientWidth);

      if (scrollLeft >= contentStart && scrollLeft <= endLeft) {
        this.updateMobileIndexFromScroll();
        return;
      }

      if (i + 1 < anchors.length && scrollLeft > endLeft && scrollLeft < anchors[i + 1].left) {
        var previousDistance = Math.abs(scrollLeft - endLeft);
        var nextDistance = Math.abs(anchors[i + 1].left - scrollLeft);
        var targetIndex = previousDistance <= nextDistance ? anchors[i].index : anchors[i + 1].index;
        var targetLeft = previousDistance <= nextDistance ? endLeft : anchors[i + 1].left;

        this.currentIndex = this.normalizeIndex(targetIndex);
        this.syncUi();
        this.mobileStage.scrollTo({
          left: targetLeft,
          behavior: 'smooth'
        });
        return;
      }
    }

    this.updateMobileIndexFromScroll();
  };

  Catalog.prototype.queueMobileSnap = function (delay) {
    var self = this;

    if (this.snapTimer) {
      window.clearTimeout(this.snapTimer);
    }

    this.snapTimer = window.setTimeout(function () {
      self.snapTimer = null;

      if (!self.mobilePointerActive) {
        self.snapMobileToSpreadEdge();
      }
    }, delay);
  };

  Catalog.prototype.handleMobilePointerStart = function () {
    this.mobilePointerActive = true;

    if (this.snapTimer) {
      window.clearTimeout(this.snapTimer);
      this.snapTimer = null;
    }
  };

  Catalog.prototype.handleMobilePointerEnd = function (event) {
    if (event && event.touches && event.touches.length > 0) return;

    this.mobilePointerActive = false;
    this.queueMobileSnap(100);
  };

  Catalog.prototype.handleMobileScroll = function () {
    var self = this;

    if (!this.mobilePointerActive) {
      this.queueMobileSnap(140);
    }

    if (this.scrollFrame) return;

    this.scrollFrame = window.requestAnimationFrame(function () {
      self.scrollFrame = null;

      if (!self.mobileStage) return;
      self.updateMobileIndexFromScroll();
    });
  };

  Catalog.prototype.renderFlipBook = function () {
    var self = this;

    if (this.usingMobileScroller) {
      this.destroyMobileScroller();
    }

    if (!this.book || !this.imageUrls.length) {
      this.renderState(this.emptyText);
      this.syncUi();
      return;
    }

    this.renderState(this.loadingText);
    this.syncUi();

    this.loadImageAt(0).then(function (firstPage) {
      if (self.destroyed) return;

      if (!firstPage || !self.canUsePageFlip()) {
        self.loadAndRender(self.currentIndex);
        return;
      }

      self.destroyFlipBook();
      self.book.innerHTML = '';

      try {
        self.flipBook = new window.St.PageFlip(self.book, {
          width: 460,
          height: 650,
          size: 'stretch',
          minWidth: 280,
          maxWidth: 520,
          minHeight: 360,
          maxHeight: 720,
          drawShadow: false,
          drawBookShadow: false,
          transparentCanvas: true,
          maxShadowOpacity: 0.14,
          flippingTime: 400,
          showCover: true,
          usePortrait: true,
          showPageCorners: false,
          mobileScrollSupport: true,
          swipeDistance: 24
        });

        if (typeof self.flipBook.on === 'function') {
          self.flipBook.on('flip', function (event) {
            var pageIndex = event && typeof event.data !== 'undefined' ? event.data : event && event.page;
            self.currentIndex = self.normalizeIndexFromPageFlip(pageIndex);
            self.syncUi();
            self.preloadAdjacent();
          });

          self.flipBook.on('changeOrientation', function () {
            self.currentIndex = self.normalizeIndexFromPageFlip(self.getPageFlipIndex());
            self.syncUi();
          });
        }

        self.flipBook.loadFromImages(self.imageUrls);
        self.currentIndex = self.normalizeIndexFromPageFlip(self.getPageFlipIndex());
        self.usingFlipBook = true;
        self.section.dataset.catalogReady = 'true';
        self.syncUi();
        self.preloadAdjacent();
      } catch (error) {
        console.error('B2B catalog: failed to initialize page flip viewer.', error);
        self.destroyFlipBook();
        self.usingFlipBook = false;
        self.loadAndRender(self.currentIndex);
      }
    });
  };

  Catalog.prototype.destroyFlipBook = function () {
    var parent = this.book ? this.book.parentNode : null;
    var nextSibling = this.book ? this.book.nextSibling : null;

    if (this.flipBook && typeof this.flipBook.destroy === 'function') {
      this.flipBook.destroy();
    }

    if (parent && this.book && !this.book.parentNode) {
      parent.insertBefore(this.book, nextSibling);
      this.book.innerHTML = '';
    }

    this.flipBook = null;
    this.usingFlipBook = false;
  };

  Catalog.prototype.getPageFlipIndex = function () {
    if (!this.flipBook || typeof this.flipBook.getCurrentPageIndex !== 'function') {
      return this.currentIndex;
    }

    return this.flipBook.getCurrentPageIndex();
  };

  Catalog.prototype.normalizeIndexFromPageFlip = function (index) {
    index = parseInt(index, 10);

    if (isNaN(index)) {
      index = 0;
    }

    return clamp(index, 0, Math.max(this.imageUrls.length - 1, 0));
  };

  Catalog.prototype.setZoom = function (zoomLevel) {
    this.zoomLevel = clamp(zoomLevel, 0.86, 1.18);

    if (this.book) {
      this.book.style.setProperty('--gm-b2b-catalog-scale', this.zoomLevel);
    }

    this.syncUi();
  };

  Catalog.prototype.zoomOut = function () {
    this.setZoom(this.zoomLevel - 0.08);
  };

  Catalog.prototype.zoomReset = function () {
    this.setZoom(1);
  };

  Catalog.prototype.zoomIn = function () {
    this.setZoom(this.zoomLevel + 0.08);
  };

  Catalog.prototype.getFlipBookVisibleIndexes = function () {
    var index = this.normalizeIndexFromPageFlip(this.getPageFlipIndex());
    var total = this.imageUrls.length;
    var mode = '';

    if (!total) return [];

    if (this.flipBook) {
      if (typeof this.flipBook.getOrientation === 'function') {
        mode = this.flipBook.getOrientation();
      } else if (this.flipBook.mode) {
        mode = this.flipBook.mode;
      }
    }

    if (mode === 'portrait') {
      return [index];
    }

    return this.getVisibleIndexes(index);
  };

  Catalog.prototype.renderState = function (message) {
    if (!this.book) return;

    this.book.innerHTML = '';

    var wrapper = document.createElement('div');
    wrapper.className = 'gm-b2b-catalog__placeholder';

    var note = document.createElement('p');
    note.className = 'gm-b2b-note';
    note.textContent = message;

    wrapper.appendChild(note);
    this.book.appendChild(wrapper);
  };

  Catalog.prototype.renderPages = function (pages) {
    if (!this.book || !pages || !pages.length) {
      this.renderState(this.emptyText);
      return;
    }

    this.book.innerHTML = '';

    var stage = document.createElement('div');
    stage.className = 'gm-b2b-catalog__fallback-stage';
    if (pages.length > 1) {
      stage.classList.add('gm-b2b-catalog__fallback-stage--spread');
    }

    for (var i = 0; i < pages.length; i++) {
      var page = document.createElement('div');
      page.className = 'gm-b2b-catalog__fallback-page gm-b2b-media';

      if (pages.length > 1) {
        page.classList.add('gm-b2b-catalog__fallback-page--spread');
      } else {
        page.classList.add('gm-b2b-catalog__fallback-page--single');
      }

      var image = document.createElement('img');
      image.src = pages[i].url;
      image.alt = this.heading + ' ' + (pages[i].index + 1);
      image.loading = 'eager';
      image.decoding = 'async';

      page.appendChild(image);
      stage.appendChild(page);
    }

    this.book.appendChild(stage);
  };

  Catalog.prototype.formatPageLabel = function () {
    var visibleIndexes = this.usingFlipBook ? this.getFlipBookVisibleIndexes() : this.getVisibleIndexes(this.currentIndex);

    if (!this.imageUrls.length) {
      return '0 / 0';
    }

    if (this.usingMobileScroller) {
      visibleIndexes = this.getVisibleIndexes(this.currentIndex);

      if (visibleIndexes.length > 1) {
        return visibleIndexes[0] + 1 + '-' + (visibleIndexes[1] + 1) + ' / ' + this.imageUrls.length;
      }

      return visibleIndexes[0] + 1 + ' / ' + this.imageUrls.length;
    }

    if (visibleIndexes.length > 1) {
      return visibleIndexes[0] + 1 + '-' + (visibleIndexes[1] + 1) + ' / ' + this.imageUrls.length;
    }

    return visibleIndexes[0] + 1 + ' / ' + this.imageUrls.length;
  };

  Catalog.prototype.syncUi = function () {
    var hasImages = this.imageUrls.length > 0;
    var currentIndex = this.usingFlipBook ? this.getPageFlipIndex() : this.currentIndex;
    var position = this.getViewPosition(currentIndex);
    var starts = this.getViewStarts();
    var isAtStart = !hasImages || position <= 0;
    var isAtEnd = !hasImages || position >= starts.length - 1;

    if (this.usingMobileScroller) {
      position = this.getViewPosition(currentIndex);
      isAtStart = !hasImages || position <= 0;
      isAtEnd = !hasImages || position >= starts.length - 1;
    }

    if (this.pageLabel) {
      this.pageLabel.textContent = this.formatPageLabel();
    }

    if (this.prevButton) {
      this.prevButton.disabled = isAtStart;
    }

    if (this.nextButton) {
      this.nextButton.disabled = isAtEnd;
    }

    if (this.progressBar) {
      this.progressBar.style.width = hasImages
        ? ((position + 1) / Math.max(starts.length, 1)) * 100 + '%'
        : '0%';
    }

    if (this.zoomOutButton) {
      this.zoomOutButton.disabled = this.zoomLevel <= 0.86;
    }

    if (this.zoomResetButton) {
      this.zoomResetButton.disabled = Math.abs(this.zoomLevel - 1) < 0.01;
    }

    if (this.zoomInButton) {
      this.zoomInButton.disabled = this.zoomLevel >= 1.18;
    }

    if (this.zoomLabel) {
      this.zoomLabel.textContent = Math.round(this.zoomLevel * 100) + '%';
    }
  };

  Catalog.prototype.goPrev = function () {
    if (!this.imageUrls.length) return;

    if (!this.initialized) {
      this.currentIndex = this.getPrevStartIndex();
      this.initialize();
      return;
    }

    if (this.usingFlipBook && this.flipBook && typeof this.flipBook.flipPrev === 'function') {
      this.flipBook.flipPrev();
      return;
    }

    if (this.usingMobileScroller) {
      if (this.getPrevStartIndex() === this.currentIndex) return;
      this.currentIndex = this.getPrevStartIndex();
      this.syncUi();
      this.scrollMobileToCurrent();
      return;
    }

    if (this.getPrevStartIndex() === this.currentIndex) return;
    this.loadAndRender(this.getPrevStartIndex());
  };

  Catalog.prototype.goNext = function () {
    if (!this.imageUrls.length) return;

    if (!this.initialized) {
      this.currentIndex = this.getNextStartIndex();
      this.initialize();
      return;
    }

    if (this.usingFlipBook && this.flipBook && typeof this.flipBook.flipNext === 'function') {
      this.flipBook.flipNext();
      return;
    }

    if (this.usingMobileScroller) {
      if (this.getNextStartIndex() === this.currentIndex) return;
      this.currentIndex = this.getNextStartIndex();
      this.syncUi();
      this.scrollMobileToCurrent();
      return;
    }

    if (this.getNextStartIndex() === this.currentIndex) return;
    this.loadAndRender(this.getNextStartIndex());
  };

  Catalog.prototype.destroy = function () {
    this.destroyed = true;
    this.section.dataset.catalogReady = 'false';

    if (this.prevButton) {
      this.prevButton.removeEventListener('click', this.boundPrev);
    }

    if (this.nextButton) {
      this.nextButton.removeEventListener('click', this.boundNext);
    }

    if (this.zoomOutButton) {
      this.zoomOutButton.removeEventListener('click', this.boundZoomOut);
    }

    if (this.zoomResetButton) {
      this.zoomResetButton.removeEventListener('click', this.boundZoomReset);
    }

    if (this.zoomInButton) {
      this.zoomInButton.removeEventListener('click', this.boundZoomIn);
    }

    if (this.mobileQuery) {
      if (typeof this.mobileQuery.removeEventListener === 'function') {
        this.mobileQuery.removeEventListener('change', this.boundViewportChange);
      } else if (typeof this.mobileQuery.removeListener === 'function') {
        this.mobileQuery.removeListener(this.boundViewportChange);
      }
    } else {
      window.removeEventListener('resize', this.boundViewportChange);
    }

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    this.destroyFlipBook();
    this.destroyMobileScroller();
  };

  var controller = {
    instances: {},
    listenersBound: false,

    mountAll: function (root) {
      var scope = root || document;
      var sections = [];

      if (scope.matches && scope.matches('[data-b2b-catalog-section]')) {
        sections.push(scope);
      }

      var nestedSections = scope.querySelectorAll ? scope.querySelectorAll('[data-b2b-catalog-section]') : [];
      for (var i = 0; i < nestedSections.length; i++) {
        sections.push(nestedSections[i]);
      }

      for (var j = 0; j < sections.length; j++) {
        var section = sections[j];
        var sectionId = section.getAttribute('data-section-id');

        if (!sectionId || this.instances[sectionId]) continue;

        var instance = new Catalog(section);
        this.instances[sectionId] = instance;
        instance.mount();
      }
    },

    unmountBySectionId: function (sectionId) {
      if (!sectionId || !this.instances[sectionId]) return;

      this.instances[sectionId].destroy();
      delete this.instances[sectionId];
    },

    bindEditorEvents: function () {
      if (this.listenersBound) return;
      this.listenersBound = true;

      var self = this;

      document.addEventListener('shopify:section:load', function (event) {
        self.mountAll(event.target);
      });

      document.addEventListener('shopify:section:unload', function (event) {
        self.unmountBySectionId(event.detail.sectionId);
      });
    }
  };

  window.GMB2BFacilityCatalog = controller;
  controller.bindEditorEvents();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      controller.mountAll(document);
    });
  } else {
    controller.mountAll(document);
  }
})(window, document);
